import { useState, useRef, useEffect } from "react";
import { Star, Camera, X, ImagePlus } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useUser } from "@/hooks/useUser";
import { createReview, uploadReviewImage, type PendingReview } from "@/lib/users-api";
import { showRateLimitToastIfNeeded } from "@/lib/rate-limit-toast";
import {
  MAX_TAGS_PER_REVIEW,
  getCategoriesFor,
  getTagsFor,
} from "@/lib/review-meta";
import { cn } from "@/lib/utils";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useToast } from "@/hooks/use-toast";

function StarRow({
  rating,
  onChange,
  interactive = false,
  size = "sm",
  ariaLabelPrefix,
}: {
  rating: number;
  onChange?: (r: number) => void;
  interactive?: boolean;
  size?: "sm" | "lg";
  ariaLabelPrefix?: string;
}) {
  // Hover / keyboard-focus preview: when the renter hovers (or tabs to) a
  // star, fill that star and every star to its left. Leaving the row reverts
  // to whatever rating has been committed. Clicking (or pressing Space/Enter)
  // commits. Mirrors the prettier per-row star input the review surface uses
  // elsewhere so the rate-each-category step feels consistent.
  const { t } = useAppTranslation();
  const [preview, setPreview] = useState(0);
  const cls = size === "lg" ? "h-9 w-9" : "h-5 w-5";
  const display = preview > 0 ? preview : rating;
  return (
    <div
      className={cn("flex", size === "lg" ? "gap-1.5" : "gap-1")}
      onMouseLeave={() => setPreview(0)}
    >
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          disabled={!interactive}
          onClick={() => onChange?.(s)}
          onMouseEnter={() => interactive && setPreview(s)}
          onFocus={() => interactive && setPreview(s)}
          onBlur={() => setPreview(0)}
          aria-label={t("reviews.star_rating_aria", { prefix: ariaLabelPrefix ? ariaLabelPrefix + " " : "", count: s })}
          className={cn(
            "transition-transform rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60",
            interactive && "cursor-pointer hover:scale-110 active:scale-95",
          )}
        >
          <Star
            className={cn(
              cls,
              s <= display
                ? "fill-brand text-brand"
                : "fill-transparent text-muted-foreground/40",
            )}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Shared Airbnb-style multi-step "Write a review" modal. Used from the
 * dashboard reviews page (pending tab), the trip detail page (banner +
 * mobile sticky CTA + `?review=1` deep link), and any other entry
 * point that needs to capture a renter -> car or host -> guest review.
 *
 * Always submits through `usersApi.createReview` (POST /api/users/me/reviews)
 * so category ratings, highlight tags, and a photo are persisted in one
 * flow. The legacy 1-5-star modal + `POST /api/bookings/review` were
 * deleted in favor of this single path.
 */
export interface ReviewSubmitResult {
  isPublished: boolean;
  revealDeadline: string | null;
}

/**
 * In-progress review drafts survive an accidental close (backdrop click,
 * Save & exit) via localStorage, keyed per trip + direction — mirrors the
 * mobile review flow's AsyncStorage drafts. Cleared on successful submit.
 */
interface ReviewDraft {
  v: number;
  rating: number;
  comment: string;
  photoUrl: string | null;
  categoryRatings: Record<string, number>;
  tags: string[];
  step: number;
  savedAt: number;
}

const DRAFT_VERSION = 1;
/** Drafts older than the 14-day review window are useless — expire at 30d. */
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function draftKey(tripId: number, direction: string): string {
  return `bac:review-draft:${tripId}:${direction}`;
}

function readDraft(tripId: number, direction: string): ReviewDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(tripId, direction));
    if (!raw) return null;
    const draft = JSON.parse(raw) as ReviewDraft;
    if (!draft || typeof draft !== "object" || draft.v !== DRAFT_VERSION) return null;
    if (typeof draft.savedAt !== "number" || Date.now() - draft.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(draftKey(tripId, direction));
      return null;
    }
    return draft;
  } catch {
    // A corrupt draft (or blocked storage) must never break the modal.
    return null;
  }
}

function writeDraft(tripId: number, direction: string, draft: Omit<ReviewDraft, "v" | "savedAt">): void {
  try {
    const hasContent =
      draft.rating > 0 ||
      draft.comment.length > 0 ||
      draft.photoUrl !== null ||
      draft.tags.length > 0 ||
      Object.values(draft.categoryRatings).some((n) => n > 0);
    if (!hasContent) {
      localStorage.removeItem(draftKey(tripId, direction));
      return;
    }
    localStorage.setItem(
      draftKey(tripId, direction),
      JSON.stringify({ ...draft, v: DRAFT_VERSION, savedAt: Date.now() } satisfies ReviewDraft),
    );
  } catch {
    // Storage full / blocked — losing a draft is annoying, not fatal.
  }
}

function clearDraft(tripId: number, direction: string): void {
  try {
    localStorage.removeItem(draftKey(tripId, direction));
  } catch {
    // ignore
  }
}

/** Server bounds for review_text (POST /api/users/me/reviews). */
const MIN_REVIEW_CHARS = 20;
const MAX_REVIEW_CHARS = 2000;

export function WriteReviewModal({
  pending,
  open,
  onClose,
  onSubmitted,
}: {
  pending: PendingReview | null;
  open: boolean;
  onClose: () => void;
  onSubmitted?: (tripId: number, result?: ReviewSubmitResult) => void;
}) {
  const { session } = useUser();
  const { t } = useAppTranslation();
  const { toast } = useToast();
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [categoryRatings, setCategoryRatings] = useState<Record<string, number>>({});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  // Which trip+direction the current in-memory state belongs to, so a draft
  // is restored exactly once per open and never bleeds across trips.
  const hydratedKeyRef = useRef<string | null>(null);

  const tripId = pending?.trip_id ?? null;
  const draftDirection = pending?.direction ?? null;

  // Restore a saved draft when the modal opens (or opens for a new trip).
  useEffect(() => {
    if (!open || tripId === null || !draftDirection) {
      hydratedKeyRef.current = null;
      return;
    }
    const key = draftKey(tripId, draftDirection);
    if (hydratedKeyRef.current === key) return;
    hydratedKeyRef.current = key;
    const draft = readDraft(tripId, draftDirection);
    setNewRating(draft?.rating ?? 0);
    setNewComment(draft?.comment ?? "");
    setPhotoUrl(draft?.photoUrl ?? null);
    setCategoryRatings(draft?.categoryRatings ?? {});
    setSelectedTags(Array.isArray(draft?.tags) ? draft.tags : []);
    setStep(
      draft && typeof draft.step === "number"
        ? (Math.min(3, Math.max(0, draft.step)) as 0 | 1 | 2 | 3)
        : 0,
    );
  }, [open, tripId, draftDirection]);

  // Write-through: any change while open updates the stored draft, so a
  // backdrop click or Save & exit can never lose typed input.
  useEffect(() => {
    if (!open || tripId === null || !draftDirection) return;
    if (hydratedKeyRef.current !== draftKey(tripId, draftDirection)) return;
    writeDraft(tripId, draftDirection, {
      rating: newRating,
      comment: newComment,
      photoUrl,
      categoryRatings,
      tags: selectedTags,
      step,
    });
  }, [open, tripId, draftDirection, newRating, newComment, photoUrl, categoryRatings, selectedTags, step]);

  if (!open || !pending) return null;

  /** Close without wiping the stored draft — the "Save & exit" affordance. */
  function saveAndClose() {
    hydratedKeyRef.current = null;
    onClose();
  }

  function resetAndClose() {
    setNewRating(0);
    setNewComment("");
    setPhotoUrl(null);
    setUploadingPhoto(false);
    setCategoryRatings({});
    setSelectedTags([]);
    setStep(0);
    hydratedKeyRef.current = null;
    onClose();
  }

  function toggleTag(slug: string) {
    setSelectedTags((prev) => {
      if (prev.includes(slug)) return prev.filter((tg) => tg !== slug);
      if (prev.length >= MAX_TAGS_PER_REVIEW) return prev;
      return [...prev, slug];
    });
  }

  async function handlePhotoFile(file: File | null | undefined) {
    if (!file || !session?.access_token) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t("common.error"),
        description: t("reviews.photo_too_large"),
        variant: "destructive",
      });
      return;
    }
    setUploadingPhoto(true);
    try {
      const { url } = await uploadReviewImage(session.access_token, file);
      setPhotoUrl(url);
    } catch (err) {
      if (showRateLimitToastIfNeeded(err, t)) {
        setUploadingPhoto(false);
        return;
      }
      console.warn("Failed to upload review photo:", err);
      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : t("reviews.photo_upload_failed"),
        variant: "destructive",
      });
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function handleSubmit() {
    if (!session?.access_token || !pending || newRating < 1 || submitting) return;
    setSubmitting(true);
    try {
      const catPayload = Object.entries(categoryRatings)
        .filter(([, n]) => n >= 1 && n <= 5)
        .map(([category, rating]) => ({ category, rating }));
      const result = await createReview(
        session.access_token,
        pending.trip_id,
        newRating,
        newComment,
        photoUrl,
        {
          categoryRatings: catPayload.length > 0 ? catPayload : undefined,
          tags: selectedTags.length > 0 ? selectedTags : undefined,
        },
      );
      void import("@/lib/analytics").then(({ track }) => {
        track("review_submitted", {
          trip_id: pending.trip_id,
          rating: newRating,
          target: pending.direction === "host_to_guest" ? "renter" : "host",
          has_photo: !!photoUrl,
        });
      });
      const submittedTripId = pending.trip_id;
      const submitResult: ReviewSubmitResult = {
        isPublished: result?.is_published ?? true,
        revealDeadline: result?.reveal_deadline ?? null,
      };
      clearDraft(submittedTripId, pending.direction);
      resetAndClose();
      onSubmitted?.(submittedTripId, submitResult);
    } catch (err) {
      if (showRateLimitToastIfNeeded(err, t)) {
        setSubmitting(false);
        return;
      }
      console.warn("Failed to submit review:", err);
      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : t("reviews.submit_failed"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const direction = pending.direction === "host_to_guest" ? "host_to_guest" : "guest_to_car";
  const cats = getCategoriesFor(direction);
  const tagOptions = getTagsFor(direction);
  const isLast = step === 3;
  const canAdvance =
    step === 0 ? newRating >= 1
    : step === 3
      ? // The review text is optional (stars alone are enough) — block only a
        // non-empty review below the server minimum, mirroring the API + mobile.
        (newComment.trim().length === 0 || newComment.trim().length >= MIN_REVIEW_CHARS) && !uploadingPhoto
    : true;
  const goNext = () => {
    if (isLast) { void handleSubmit(); return; }
    setStep((s) => (Math.min(3, s + 1) as 0 | 1 | 2 | 3));
  };
  const goBack = () => setStep((s) => (Math.max(0, s - 1) as 0 | 1 | 2 | 3));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="write-review-title"
      className="fixed inset-0 z-50 flex items-end justify-center material-scrim sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) saveAndClose(); }}
      data-testid="write-review-modal"
    >
      <div className="flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-card shadow-2xl sm:mx-4 sm:rounded-2xl">
        <div className="relative h-44 w-full shrink-0 bg-muted">
          {pending.car?.primary_image_url ? (
            <img
              src={pending.car.primary_image_url}
              alt={`${pending.car.year} ${pending.car.make} ${pending.car.model}`}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
              <ImagePlus className="h-10 w-10" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
          <button
            type="button"
            onClick={saveAndClose}
            disabled={submitting}
            aria-label={t("reviews.save_and_exit", { defaultValue: "Save & exit" })}
            // material: intentional — interactive close button with custom hover state (bg-black/40 → /60); not a passive chip
            className="cursor-pointer absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition hover:bg-black/60 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
            <h3 id="write-review-title" className="text-xl font-semibold drop-shadow">
              {t("reviews.write_review")}
            </h3>
            {pending.car && (
              <p className="mt-0.5 text-sm font-medium text-white/95 drop-shadow">
                {pending.car.year} {pending.car.make} {pending.car.model}
              </p>
            )}
            {pending.reviewee && (
              <p className="mt-0.5 text-xs text-white/80 drop-shadow">
                {t("reviews.with_host", { name: pending.reviewee.first_name })}
              </p>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6">
          <div className="flex gap-1.5" role="progressbar" aria-valuemin={1} aria-valuemax={4} aria-valuenow={step + 1}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn(
                  "h-1 flex-1 rounded-full transition-colors",
                  i <= step ? "bg-brand" : "bg-muted",
                )}
              />
            ))}
          </div>

          {step === 0 && (
            <div className="rounded-xl border border-border bg-muted/30 p-5 text-center">
              <p className="text-base font-semibold text-foreground">{t("reviews.rating_prompt")}</p>
              <div className="mt-4 flex justify-center">
                <StarRow rating={newRating} onChange={setNewRating} interactive size="lg" />
              </div>
              <p className="mt-2 min-h-[1.25rem] text-xs text-muted-foreground">
                {newRating > 0 ? t(`reviews.rating_label_${newRating}`) : t("reviews.tap_to_rate")}
              </p>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="mb-3 text-base font-semibold">
                {t("reviews.rate_categories", { defaultValue: "Rate each part of the experience" })}
              </p>
              <div className="space-y-2 rounded-xl border border-border p-3">
                {cats.map((cat) => {
                  const Icon = cat.icon;
                  const value = categoryRatings[cat.slug] || 0;
                  return (
                    <div key={cat.slug} className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-sm text-foreground">{cat.label}</span>
                      <StarRow
                        rating={value}
                        onChange={(n) => setCategoryRatings((p) => ({ ...p, [cat.slug]: n }))}
                        interactive
                        ariaLabelPrefix={cat.label}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="mb-3 text-base font-semibold">
                {t("reviews.pick_highlights", { defaultValue: "Pick up to 3 highlights" })}
              </p>
              <div className="flex flex-wrap gap-2">
                {tagOptions.map((tag) => {
                  const active = selectedTags.includes(tag.slug);
                  const disabled = !active && selectedTags.length >= MAX_TAGS_PER_REVIEW;
                  return (
                    <button
                      key={tag.slug}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleTag(tag.slug)}
                      aria-pressed={active}
                      data-testid={`review-tag-${tag.slug}`}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border-2 px-3.5 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60",
                        active
                          ? "border-brand bg-brand text-brand-foreground shadow-sm"
                          : "border-border bg-background text-foreground hover:border-brand/50 hover:bg-muted",
                        disabled && "opacity-40 cursor-not-allowed hover:border-border hover:bg-background",
                      )}
                    >
                      {tag.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                {t("reviews.tags_selected", {
                  defaultValue: "{{count}} of {{max}} selected",
                  count: selectedTags.length,
                  max: MAX_TAGS_PER_REVIEW,
                })}
              </p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <label htmlFor="review-text" className="mb-1.5 block text-base font-semibold">
                  {t("reviews.your_review")}
                </label>
                {/* One prose sentence naming who sees this and when. */}
                <p className="mb-2 text-xs text-muted-foreground">
                  {pending.direction === "host_to_guest"
                    ? t("reviews.publish_prose_guest", {
                        defaultValue:
                          "We'll show this to other hosts once {{name}} writes their review, or the review window ends. Then we'll publish it on their profile.",
                        name: pending.reviewee?.first_name ?? t("reviews.your_guest_fallback", { defaultValue: "your guest" }),
                      })
                    : t("reviews.publish_prose_car", {
                        defaultValue:
                          "We'll share this with other renters once {{name}} writes their review, or the review window ends. Then we'll publish it on the car's page.",
                        name: pending.reviewee?.first_name ?? t("reviews.your_host_fallback", { defaultValue: "your host" }),
                      })}
                </p>
                <textarea
                  id="review-text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={4}
                  maxLength={MAX_REVIEW_CHARS}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                  placeholder={t("reviews.share_experience")}
                />
                {/* Positive counter framing (replit.md design conventions). */}
                <p className="mt-1 text-xs text-muted-foreground">
                  {newComment.length === 0
                    ? t("reviews.review_optional_hint", { defaultValue: "Optional. Stars alone are enough." })
                    : newComment.length < MIN_REVIEW_CHARS
                      ? t("reviews.chars_remaining", { count: MIN_REVIEW_CHARS - newComment.length })
                      : t("reviews.chars_available", {
                          defaultValue: "{{count}} characters available",
                          count: MAX_REVIEW_CHARS - newComment.length,
                        })}
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("reviews.add_photo_label")}</label>
                {photoUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={photoUrl}
                      alt={t("reviews.your_uploaded_photo")}
                      className="h-32 w-44 rounded-lg object-cover ring-1 ring-border"
                    />
                    <button
                      type="button"
                      onClick={() => setPhotoUrl(null)}
                      aria-label={t("reviews.remove_photo")}
                      className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-background shadow-md hover:bg-foreground/85"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className={cn(
                      "flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground transition-colors",
                      "hover:border-accent/60 hover:bg-accent/5 hover:text-foreground",
                      uploadingPhoto && "opacity-60",
                    )}
                  >
                    {uploadingPhoto ? (
                      <>
                        <Spinner size="sm" />
                        <span>{t("reviews.uploading_photo")}</span>
                      </>
                    ) : (
                      <>
                        <Camera className="h-5 w-5" />
                        <span>{t("reviews.add_photo_cta")}</span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    void handlePhotoFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-muted/20 px-6 py-4">
          {step > 0 ? (
            <button
              type="button"
              onClick={goBack}
              disabled={submitting}
              className="cursor-pointer rounded-md px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              {t("common.back", { defaultValue: "Back" })}
            </button>
          ) : (
            <button
              type="button"
              onClick={saveAndClose}
              disabled={submitting}
              data-testid="write-review-save-exit"
              className="cursor-pointer rounded-md px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              {t("reviews.save_and_exit", { defaultValue: "Save & exit" })}
            </button>
          )}
          <button
            type="button"
            onClick={goNext}
            disabled={!canAdvance || submitting}
            data-testid="write-review-modal-next"
            className={cn(
        "cursor-pointer inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition disabled:opacity-50",
              canAdvance && !submitting
                ? "bg-brand text-brand-foreground hover:bg-brand-hover"
                : "bg-accent text-accent-foreground",
            )}
          >
            {submitting && <Spinner size="sm" />}
            {isLast
              ? (submitting ? t("common.submitting") : t("reviews.submit_review"))
              : t("common.next", { defaultValue: "Next" })}
          </button>
        </div>
      </div>
    </div>
  );
}
